import React, {
  ReactNode,
  useState,
  ChangeEvent,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";
import "../../components/PaymentGatewayTable/TransactionTable.scss";
import { FilterIcon, RefreshIcon } from "../Icons/paymentGateway";
import { CalendarIcon, DownloadIcon } from "../Icons/CheckOutLogo";
import DataSpinner from "../../CustomComponent/Spinner/Spinner";
import {
  getImageForPaymentMethod,
  handleLangugeDirection,
} from "../commonFunctions";
import DateRangePickerComponent from "../../CustomComponent/DateRangePicker/DateRangePicker";
import { CSVLink } from "react-csv";
import "./ReTable.scss";
import CustomPagination from "../Paggination/Pagination";
import { useTranslation } from "react-i18next";
import { lang } from "../../models";

interface TableColumn {
  label: string | React.ReactNode;
  key: string | number;
  className?: string;
  render?: (data: any) => ReactNode;
  /** When set, used for CSV export instead of the raw row[key] value */
  exportValue?: (data: any) => string | number | boolean | null | undefined;
  /** Set false to omit a column from CSV export (e.g. action menus) */
  exportable?: boolean;
}

const NON_EXPORTABLE_COLUMN_KEYS = new Set(["", "action", "actions"]);

const isExportableColumn = (column: TableColumn) => {
  if (column.exportable === false) {
    return false;
  }
  const key = String(column.key ?? "")
    .trim()
    .toLowerCase();
  return key.length > 0 && !NON_EXPORTABLE_COLUMN_KEYS.has(key);
};

const getColumnExportHeader = (column: TableColumn) => {
  if (typeof column.label === "string" && column.label.trim()) {
    return column.label;
  }
  return String(column.key);
};

const getColumnExportCellValue = (row: any, column: TableColumn) => {
  if (column.exportValue) {
    const value = column.exportValue(row);
    if (value === null || value === undefined) {
      return "-";
    }
    return value;
  }
  const value = row[column.key as string];
  if (value === null || value === undefined) {
    return "-";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    return value;
  }
  return String(value);
};

const buildCsvExportData = (data: any[], columns: TableColumn[]) => {
  const exportColumns = columns.filter(isExportableColumn);
  if (!Array.isArray(data) || exportColumns.length === 0) {
    return [];
  }
  return data.map((row) => {
    const record: Record<string, string | number | boolean> = {};
    exportColumns.forEach((column) => {
      record[getColumnExportHeader(column)] = getColumnExportCellValue(
        row,
        column,
      );
    });
    return record;
  });
};

interface DateRangeType {
  startDate: string;
  endDate: string;
}

interface TableProps {
  data: any[];
  columns: TableColumn[];
  pageSize?: number | any;
  onPageSizeChange?: (newPageSize: number) => void;
  currentPageProp: number;
  totalMatches: number;
  totalPages: number;
  resultPageLength: number;
  error?: string | undefined | null;
  onPageChange?: (newPage: number) => void;
  onRefresh?: (refresh: number) => void;
  loading?: boolean;
  showDateFilter?: boolean;
  showPagination?: boolean;
  onDateRangeSelect?: (dateRange: DateRangeType) => void;
  isTableDetailRow?: boolean;
  isPagination?: boolean;
  showHeader?: boolean;
  showRefreshButton?: boolean;
  showCsvDownload?: boolean;
  showFilterButton?: boolean;
  onFilter?: (filter: number) => void;
  renderRowDetails?: (rowIndex: any) => React.ReactNode;
  page?: number | any;
  customFooter?: any;
  onDownloadFullData?: () => Promise<void>;
}

const ReTable: React.FC<TableProps> = ({
  data,
  columns,
  pageSize,
  totalMatches,
  totalPages,
  loading,
  currentPageProp,
  resultPageLength,
  onDateRangeSelect,
  showDateFilter = false,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onFilter,
  isPagination,
  isTableDetailRow = true,
  showHeader = true,
  showRefreshButton = true,
  showCsvDownload = true,
  showFilterButton = true,
  renderRowDetails,
  error,
  page,
  customFooter,
  onDownloadFullData,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshdata, setrefreshData] = useState(0);
  const [filterData, setFilterdata] = useState(0);
  const [isDateVisible, setIsDateVisible] = useState(false);
  const tableElement = useRef(null);
  var headerRef = useRef();
  const { t } = useTranslation();

  useLayoutEffect(() => {
    setCurrentPage((page ?? 0) + 1);
  }, [page]);

  const tableHeader: any[] = useMemo(() => {
    return columns.map((item, index) => ({
      label: item.label,
      key: index,
      ref: headerRef,
    }));
  }, [columns]);

  const csvExportData = useMemo(
    () => buildCsvExportData(data, columns),
    [data, columns],
  );

  const csvFilename = useMemo(() => {
    const firstExportColumn = columns.find(isExportableColumn);
    const baseName = firstExportColumn
      ? getColumnExportHeader(firstExportColumn)
      : "export";
    return `${baseName}.csv`;
  }, [columns]);

  const handleRefresh = () => {
    setCurrentPage(1);
    setrefreshData((p) => p + 1);
    onRefresh && onRefresh(refreshdata);
  };

  const handleFilter = () => {
    setCurrentPage(1);
    setFilterdata((p) => p + 1);
    onFilter && onFilter(refreshdata);
  };

  const handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedPageSize = parseInt(e.target.value, 10);
    onPageSizeChange && onPageSizeChange(selectedPageSize);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    onPageChange && onPageChange(pageNumber);
  };

  const renderCell = (data: any, key: string | number) => {
    if (data === null) {
      return <>-</>;
    } else if (typeof data === "number") {
      return data.toString();
    } else if (typeof data === "boolean") {
      return data ? "Yes" : "No";
    } else if (typeof data === "string") {
      return data;
    }
  };

  const getDateRange = (dateRange: any) => {
    if (onDateRangeSelect) {
      onDateRangeSelect(dateRange);
    }
    setIsDateVisible(false);
  };
  return (
    <div className="ReTableWrapper">
      {isTableDetailRow && (
        <div className="tableDetailRow">
          {isPagination ? (
            <div className="transactionCount">
              {t("show")}
              <select
                id="language"
                name="page-size"
                className="select-wrapper"
                onChange={handlePageSizeChange}
                required
                value={pageSize}
                style={{
                  backgroundPosition: `${handleLangugeDirection(
                    lang,
                    "backgroundPosition",
                  )} center`,
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </div>
          ) : (
            <div></div>
          )}

          <div style={{ display: "flex", gap: "10px" }}>
            {showDateFilter && (
              <div className="date-advanced ">
                <DateRangePickerComponent
                  getDateRange={getDateRange}
                  title={t("filterByDate")}
                  customButton={() => {
                    return (
                      <button className="button-secondary-light">
                        <CalendarIcon />
                      </button>
                    );
                  }}
                />
              </div>
            )}
            {showCsvDownload &&
              (onDownloadFullData ? (
                <button
                  className="button-secondary-light"
                  disabled={loading}
                  onClick={onDownloadFullData}
                >
                  <DownloadIcon color="#000000" />
                </button>
              ) : (
                <CSVLink data={csvExportData} filename={csvFilename}>
                  <button
                    className="button-secondary-light"
                    disabled={data?.length <= 0}
                  >
                    <DownloadIcon color="#000000" />
                  </button>
                </CSVLink>
              ))}
            {showRefreshButton && (
              <button
                className="button-secondary-light"
                onClick={handleRefresh}
              >
                <RefreshIcon />
              </button>
            )}
            {showFilterButton && (
              <button className="button-secondary-light" onClick={handleFilter}>
                <FilterIcon />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="TableWrapper">
        <div className="tableWrapper">
          <table className="table" ref={tableElement}>
            {showHeader && (
              <thead className="thead">
                <tr>
                  {tableHeader?.map((column, index) => (
                    <th key={index} ref={column.ref}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
            )}

            {loading ? (
              <tbody className="tbody">
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: "center" }}>
                    <DataSpinner />
                  </td>
                </tr>
              </tbody>
            ) : error ? (
              <tbody className="tbody">
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: "center" }}>
                    {error}
                  </td>
                </tr>
              </tbody>
            ) : data && data.length > 0 ? (
              <tbody className="tbody">
                {data?.map((item, index) => (
                  <React.Fragment key={index}>
                    <tr>
                      {columns?.map((column, index) => (
                        <td key={index}>
                          {column.render
                            ? column.render(item)
                            : renderCell(item[column.key], column.key)}
                        </td>
                      ))}
                    </tr>
                    {renderRowDetails && renderRowDetails(item)}
                  </React.Fragment>
                ))}
              </tbody>
            ) : (
              <tbody className="tbody">
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              </tbody>
            )}
          </table>
          {customFooter && customFooter()}
        </div>
      </div>

      {data?.length > 0 && isPagination && (
        <div className="pagination">
          <span className="page-info">
            Showing <strong>{currentPageProp + 1}</strong> to{" "}
            <strong>{currentPageProp + resultPageLength}</strong> of{" "}
            <strong>{totalMatches}</strong> entries
          </span>
          <CustomPagination
            onPageChange={(currPage: number) => {
              handlePageChange(currPage + 1);
            }}
            currentPage={page + 1}
            pageSize={pageSize}
            totalCount={totalMatches}
            setCurrentpage={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default ReTable;
